import React from 'react';
import { HashRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Discovery from './pages/Discovery';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import Messages from './pages/Messages';
import Chat from './pages/Chat';
import Admin from './pages/Admin';
import Login from './pages/Login';
import Register from './pages/Register';
import Friends from './pages/Friends';
import { AppProvider, useAppContext } from './context/AppContext';

const LoadingScreen = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#fcfaff] dark:bg-dark-bg gap-4">
    <div className="w-16 h-16 bg-brand-600 rounded-[1.5rem] flex items-center justify-center shadow-2xl shadow-brand-200 animate-pulse">
      <span className="text-white font-black text-3xl leading-none tracking-tighter">N</span>
    </div>
    <div className="w-8 h-8 border-4 border-brand-200 dark:border-brand-900/30 border-t-brand-600 rounded-full animate-spin"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { currentUser, isInitialized } = useAppContext();
  if (!isInitialized) return <LoadingScreen />;
  return currentUser ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { currentUser, isInitialized } = useAppContext();
  if (!isInitialized) return <LoadingScreen />;
  return currentUser?.role === 'admin' ? children : <Navigate to="/" replace />;
};

const PublicRoute = ({ children }) => {
  const { currentUser, isInitialized } = useAppContext();
  if (!isInitialized) return <LoadingScreen />;
  return currentUser ? <Navigate to="/" replace /> : children;
};

const Layout = () => (
  <div className="min-h-screen bg-[#fcfaff] dark:bg-dark-bg relative overflow-x-hidden transition-colors duration-300">
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] dark:opacity-[0.05]"></div>
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-200/30 dark:bg-brand-900/20 blur-[120px] rounded-full animate-blob"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand-100/30 dark:bg-brand-800/10 blur-[120px] rounded-full animate-blob animation-delay-2000"></div>
    </div>
    <div className="relative z-10 flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  </div>
);

function App() {
  return (
    <AppProvider>
      <HashRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Discovery />} />
            <Route path="friends" element={<Friends />} />
            <Route path="profile/edit" element={<EditProfile />} />
            <Route path="profile/:id" element={<Profile />} />
            <Route path="messages" element={<Messages />} />
            <Route path="messages/:id" element={<Chat />} />
            <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </HashRouter>
    </AppProvider>
  );
}

export default App;